import { useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { useArtist, usePageMeta } from "@/hooks";
import { useUIStore } from "@/stores/uiStore";
import { cn, toTitleCase } from "@/lib/utils";
import { buildCloudinaryUrl } from "@/lib/cloudinaryUrl";
import {
  Loader2,
  ArrowLeft,
  Calendar,
  Check,
  X,
  LayoutGrid,
} from "lucide-react";
import { ThumbnailCarousel } from "@/components/ThumbnailCarousel";
import { SocialLinksBar } from "@/components/SocialLinksBar";
import { ArtistBanner } from "@/components/artists/ArtistBanner";
import { Lightbox } from "@/components/ImageGallery/Lightbox";
import { WaitlistModal } from "@/components/WaitlistModal";
import { ArtistReviewsSection } from "@/components/ArtistReviewsSection";
import { JsonLd } from "@/components/common/JsonLd";
import { buildArtistGraph } from "@/lib/structuredData";
import type { ContentRating } from "@/types";
import type { GalleryImageWithArtist } from "@/components/ImageGallery/types";

// Darker shades for WCAG AA contrast with white text (fix for M-A11Y-1).
const statusColors: Record<string, string> = {
  open: "bg-green-700",
  closed: "bg-red-700",
  waitlist: "bg-amber-700",
};

const statusLabels: Record<string, string> = {
  open: "Open for Commissions",
  closed: "Currently Closed",
  waitlist: "Waitlist Open",
};

const ratingConfig: Record<ContentRating, { label: string; icon: string; activeClass: string }> = {
  safe: {
    label: "Safe",
    icon: "🎨",
    activeClass: "bg-green-500 border-green-500 text-white"
  },
  suggestive: {
    label: "Suggestive",
    icon: "🕯️",
    activeClass: "bg-amber-500 border-amber-500 text-black"
  },
  nsfw: {
    label: "NSFW",
    icon: "🔥",
    activeClass: "bg-red-500 border-red-500 text-white"
  },
};

export function ArtistPage() {
  const { slug } = useParams<{ slug: string }>();
  const { selectedContentRatings, toggleContentRating, getContentRatingParam } = useUIStore();
  const [selectedTag, setSelectedTag] = useState<string | null>(null);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [isWaitlistOpen, setIsWaitlistOpen] = useState(false);
  const contentRatingParam = getContentRatingParam();
  const { data: artist, isLoading, error } = useArtist(slug || "", contentRatingParam);

  usePageMeta({
    title: artist
      ? `${artist.name} — Art Commissions | StudioComm`
      : "Artist — Art Commissions | StudioComm",
    description: artist
      ? `Commission hand-drawn art from ${artist.name}${artist.specialty ? ` — ${artist.specialty}` : ""}. View portfolio, prices and availability at StudioComm.`
      : undefined,
    canonicalPath: slug ? `/artist/${slug}` : undefined,
  });

  const sortedPortfolio = useMemo(() => {
    if (!artist?.portfolio) return [];
    // Respect the artist's curated order; fall back to recency for
    // uncurated portfolios (where every display_order is 0).
    return [...artist.portfolio].sort((a, b) => {
      const orderA = a.displayOrder ?? 0;
      const orderB = b.displayOrder ?? 0;
      if (orderA !== orderB) return orderA - orderB;
      const dateA = a.uploadedAt ? new Date(a.uploadedAt).getTime() : 0;
      const dateB = b.uploadedAt ? new Date(b.uploadedAt).getTime() : 0;
      return dateB - dateA;
    });
  }, [artist?.portfolio]);

  // Tags actually present on this artist's images, most common first —
  // these drive the filter chips above the showcase.
  const portfolioTags = useMemo(() => {
    const counts = new Map<string, number>();
    for (const img of sortedPortfolio) {
      for (const tag of img.tags) {
        const key = tag.trim().toLowerCase();
        if (key) counts.set(key, (counts.get(key) ?? 0) + 1);
      }
    }
    return [...counts.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([tag, count]) => ({ tag, count }));
  }, [sortedPortfolio]);

  const carouselImages = useMemo(() => {
    if (!selectedTag) return sortedPortfolio;
    const matched = sortedPortfolio.filter((img) =>
      img.tags.some((tag) => tag.toLowerCase() === selectedTag.toLowerCase())
    );
    // A sidebar specialization may not exist as an image tag — don't
    // blank the showcase in that case.
    return matched.length > 0 ? matched : sortedPortfolio;
  }, [sortedPortfolio, selectedTag]);

  const lightboxImages: GalleryImageWithArtist[] = useMemo(() => {
    if (!artist) return [];
    return sortedPortfolio.map((img) => ({
      image: {
        id: img.id,
        url: img.url,
        thumbnail: img.thumbnail,
        altText: img.altText,
        tags: img.tags,
        contentRating: img.contentRating,
        created_at: img.uploadedAt,
        width: img.width ?? undefined,
        height: img.height ?? undefined,
      },
      artist: {
        id: artist.id,
        slug: artist.slug,
        name: artist.name,
        specialty: artist.specialty,
        status: artist.status,
        tags: artist.tags,
        willDraw: artist.willDraw,
        wontDraw: artist.wontDraw,
        links: artist.links,
        prices: artist.prices,
        additionalFees: artist.additionalFees,
        visibleFees: artist.visibleFees,
        priceRange: artist.priceRange,
        deliveryDays: artist.deliveryDays,
        nsfwLevel: artist.nsfwLevel,
        profilePicture: artist.profilePicture,
        coverImage: artist.coverImage,
        portfolio: [],
        socialLinks: artist.socialLinks,
      },
    }));
  }, [artist, sortedPortfolio]);

  const handleImageClick = (carouselIndex: number) => {
    const clickedImage = carouselImages[carouselIndex];
    const fullIndex = sortedPortfolio.findIndex((img) => img.id === clickedImage.id);
    setLightboxIndex(fullIndex >= 0 ? fullIndex : carouselIndex);
  };

  const socialLinks = useMemo(() => {
    if (!artist) return {};
    if (artist.socialLinks && Object.keys(artist.socialLinks).length > 0) {
      return artist.socialLinks;
    }
    const links: Record<string, string> = {};
    if (artist.links.twitter) links.twitter = artist.links.twitter;
    if (artist.links.website) links.website = artist.links.website;
    return links;
  }, [artist]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-[var(--color-primary)]" />
        <span className="ml-3 text-[var(--color-text-muted)]">
          Loading artist profile...
        </span>
      </div>
    );
  }

  if (error || !artist) {
    return (
      // data-prerender="skip": never snapshot a not-found page as a 200 (soft 404)
      <div data-prerender="skip" className="flex flex-col items-center justify-center min-h-[60vh] px-4">
        <h1 className="text-2xl font-bold text-[var(--color-text)] mb-2">
          Artist Not Found
        </h1>
        <p className="text-[var(--color-text-muted)] mb-6">
          The artist you're looking for doesn't exist or has been removed.
        </p>
        <Link
          to="/"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[var(--color-primary)] text-[var(--color-bg)] hover:bg-[var(--color-primary-light)] transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Home
        </Link>
      </div>
    );
  }

  const coverImageRaw = artist.coverImage || artist.portfolio[0]?.url;
  const profilePicture = buildCloudinaryUrl(artist.profilePicture, { width: 200 }) || artist.profilePicture;
  const artistNsfwLevel = artist.nsfwLevel || "safe";
  const showSuggestive = artistNsfwLevel === "suggestive" || artistNsfwLevel === "nsfw";
  const showNsfw = artistNsfwLevel === "nsfw";
  const showFilters = showSuggestive || showNsfw;
  const hasSocialLinks = Object.keys(socialLinks).length > 0;

  return (
    <div className="min-h-screen">
      <JsonLd data={buildArtistGraph(artist)} />
      {/* Hero Cover Image */}
      <ArtistBanner
        imageUrl={coverImageRaw}
        // Framing belongs to the uploaded cover; the portfolio fallback is auto-framed.
        framing={artist.coverImage ? artist.coverFraming ?? null : null}
        artistName={artist.name}
      />

      {/* Profile Header */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 -mt-20 relative z-10">
        <div className="bg-[var(--color-bg-card)] rounded-2xl border border-[var(--color-border)] shadow-xl overflow-hidden">
          <div className="p-6 pb-4">
            <div className="flex items-center gap-4">
              {profilePicture ? (
                <img
                  src={profilePicture}
                  alt={artist.name}
                  className="h-20 w-20 sm:h-24 sm:w-24 rounded-full object-cover ring-4 ring-[var(--color-bg)] shadow-lg flex-shrink-0"
                  decoding="async"
                />
              ) : (
                <div className="h-20 w-20 sm:h-24 sm:w-24 rounded-full bg-[var(--color-primary)]/20 flex items-center justify-center ring-4 ring-[var(--color-bg)] shadow-lg flex-shrink-0">
                  <span className="text-2xl sm:text-3xl font-bold text-[var(--color-primary)]">
                    {artist.name.charAt(0).toUpperCase()}
                  </span>
                </div>
              )}

              <div className="flex-1 min-w-0">
                <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-[var(--color-text)] truncate">
                  {artist.name}
                </h1>
                <p className="text-sm sm:text-base text-[var(--color-text-muted)] truncate">
                  {artist.specialty}
                </p>
              </div>

              <div className="hidden sm:block flex-shrink-0">
                <span
                  className={cn(
                    "inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium text-white",
                    statusColors[artist.status]
                  )}
                >
                  <span className="h-2 w-2 rounded-full bg-white/80 animate-pulse" />
                  {statusLabels[artist.status]}
                </span>
              </div>
            </div>

            <div className="sm:hidden mt-3">
              <span
                className={cn(
                  "inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium text-white",
                  statusColors[artist.status]
                )}
              >
                <span className="h-2 w-2 rounded-full bg-white/80 animate-pulse" />
                {statusLabels[artist.status]}
              </span>
            </div>
          </div>

          {/* Social Links Bar + Primary CTA (fix for H-UX-1: Commission CTA above fold) */}
          <div className="px-6 py-3 bg-[var(--color-bg)]/50 border-t border-[var(--color-border)] flex flex-wrap items-center gap-3">
            {hasSocialLinks && <SocialLinksBar links={socialLinks} />}
            <Link
              to={`/artist/${slug}/gallery`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[var(--color-primary)]/10 border border-[var(--color-primary)]/30 text-[var(--color-primary)] hover:bg-[var(--color-primary)] hover:text-white transition-colors text-sm font-medium"
            >
              <LayoutGrid className="h-4 w-4" />
              View Full Gallery
            </Link>
            {/* Primary CTA duplicated above fold — same target as sidebar button */}
            {artist.status === "open" && (
              <Link
                to={`/commission?artist=${artist.slug}`}
                className="ml-auto inline-flex items-center justify-center gap-1.5 h-9 px-4 rounded-full bg-[var(--color-primary)] text-[var(--color-bg)] font-semibold hover:bg-[var(--color-primary-light)] transition-colors shadow"
              >
                Request Commission
              </Link>
            )}
            {artist.status === "waitlist" && (
              <button
                onClick={() => setIsWaitlistOpen(true)}
                className="ml-auto inline-flex items-center justify-center gap-1.5 h-9 px-4 rounded-full bg-amber-500 text-white font-semibold hover:bg-amber-600 transition-colors shadow"
              >
                Join Waitlist
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Content Grid */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-6">
            {artist.tags.length > 0 && (
              <div className="p-4 rounded-xl bg-[var(--color-bg-card)] border border-[var(--color-border)]">
                <h2 className="font-semibold text-[var(--color-text)] mb-3">
                  Specializations
                </h2>
                <div className="flex flex-wrap gap-2">
                  {artist.tags.map((tag) => (
                    <button
                      key={tag}
                      onClick={() => setSelectedTag(selectedTag === tag ? null : tag)}
                      className={cn(
                        "px-2.5 py-1 text-sm rounded-lg transition-all cursor-pointer",
                        selectedTag === tag
                          ? "bg-[var(--color-primary)] text-[var(--color-bg)] ring-2 ring-[var(--color-primary)] ring-offset-2 ring-offset-[var(--color-bg-card)]"
                          : "bg-[var(--color-primary)]/10 text-[var(--color-primary)] hover:bg-[var(--color-primary)]/20"
                      )}
                    >
                      {toTitleCase(tag)}
                    </button>
                  ))}
                </div>
                {selectedTag && (
                  <button
                    onClick={() => setSelectedTag(null)}
                    className="mt-3 text-xs text-[var(--color-text-muted)] hover:text-[var(--color-text)] underline"
                  >
                    Clear filter
                  </button>
                )}
              </div>
            )}

            {(artist.willDraw.length > 0 || artist.wontDraw.length > 0) && (
              <div className="p-4 rounded-xl bg-[var(--color-bg-card)] border border-[var(--color-border)]">
                <h2 className="font-semibold text-[var(--color-text)] mb-3">
                  Preferences
                </h2>

                {artist.willDraw.length > 0 && (
                  <div className="mb-4">
                    <h3 className="text-sm text-[var(--color-text-muted)] mb-2 flex items-center gap-1.5">
                      <Check className="h-4 w-4 text-green-500" />
                      Will Draw
                    </h3>
                    <div className="flex flex-wrap gap-1.5">
                      {artist.willDraw.map((item) => (
                        <span
                          key={item}
                          className="px-2 py-0.5 text-xs rounded-md bg-green-500/10 text-green-600"
                        >
                          {item}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {artist.wontDraw.length > 0 && (
                  <div>
                    <h3 className="text-sm text-[var(--color-text-muted)] mb-2 flex items-center gap-1.5">
                      <X className="h-4 w-4 text-red-500" />
                      Won't Draw
                    </h3>
                    <div className="flex flex-wrap gap-1.5">
                      {artist.wontDraw.map((item) => (
                        <span
                          key={item}
                          className="px-2 py-0.5 text-xs rounded-md bg-red-500/10 text-red-600"
                        >
                          {item}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {artist.prices && Object.keys(artist.prices).length > 0 && (
              <div className="p-4 rounded-xl bg-[var(--color-bg-card)] border border-[var(--color-border)]">
                <h2 className="font-semibold text-[var(--color-text)] mb-3">
                  Pricing
                </h2>
                <div className="space-y-2">
                  {Object.entries(artist.prices).map(([type, price]) => (
                    <div
                      key={type}
                      className="flex items-center justify-between py-2 border-b border-[var(--color-border)] last:border-0"
                    >
                      <span className="text-sm text-[var(--color-text)]">
                        {type}
                      </span>
                      <span className="font-medium text-[var(--color-primary)]">
                        {typeof price === 'number' ? `$${price}` : price}
                      </span>
                    </div>
                  ))}
                </div>

                {artist.additionalFees &&
                  artist.visibleFees &&
                  artist.visibleFees.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-[var(--color-border)]">
                      <h3 className="text-sm text-[var(--color-text-muted)] mb-2">
                        Additional Fees
                      </h3>
                      <div className="space-y-1">
                        {artist.visibleFees.map((feeKey) => {
                          const feeValue = artist.additionalFees?.[feeKey];
                          if (feeValue === undefined) return null;

                          // Format fee key from "background_simple" to "Background Simple"
                          const formattedKey = feeKey
                            .replace(/_/g, ' ')
                            .replace(/\b\w/g, c => c.toUpperCase());

                          // Format fee value - handle various formats:
                          // - Already formatted: "+$10", "+50%"
                          // - Just numbers: "10" -> "+$10"
                          // - Percentages: "50%" -> "+50%"
                          const formatFeeValue = (value: string): string => {
                            if (value.startsWith('+')) return value;
                            if (value.includes('%')) return `+${value}`;
                            const num = parseInt(value.replace(/[^0-9]/g, ''), 10);
                            return isNaN(num) ? value : `+$${num}`;
                          };

                          return (
                            <div
                              key={feeKey}
                              className="flex items-center justify-between text-sm"
                            >
                              <span className="text-[var(--color-text-muted)]">
                                {formattedKey}
                              </span>
                              <span className="text-[var(--color-text)]">
                                {formatFeeValue(feeValue)}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                {artist.deliveryDays && (
                  <div className="mt-4 pt-4 border-t border-[var(--color-border)] flex items-center gap-2 text-sm text-[var(--color-text-muted)]">
                    <Calendar className="h-4 w-4" />
                    <span>
                      Estimated delivery:{" "}
                      <span className="text-[var(--color-text)]">
                        {artist.deliveryDays} days
                      </span>
                    </span>
                  </div>
                )}
              </div>
            )}

            {artist.status === "open" && (
              <Link
                to={`/commission?artist=${artist.slug}`}
                className="w-full inline-flex items-center justify-center h-12 px-6 rounded-xl bg-[var(--color-primary)] text-[var(--color-bg)] font-medium hover:bg-[var(--color-primary-light)] transition-colors shadow-lg"
              >
                Request Commission
              </Link>
            )}

            {artist.status === "closed" && (
              <div className="w-full flex items-center justify-center h-12 px-6 rounded-xl bg-[var(--color-bg)] border border-[var(--color-border)] text-[var(--color-text-muted)]">
                Commissions Closed
              </div>
            )}

            {artist.status === "waitlist" && (
              <button
                onClick={() => setIsWaitlistOpen(true)}
                className="w-full inline-flex items-center justify-center h-12 px-6 rounded-xl bg-amber-500 text-white font-medium hover:bg-amber-600 transition-colors shadow-lg"
              >
                Join Waitlist
              </button>
            )}
          </div>

          {/* Main Content - Portfolio Carousel */}
          <div className="lg:col-span-2">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-[var(--color-text)]">Portfolio</h2>
            </div>

            {/* Commission-type filter chips, derived from image tags */}
            {portfolioTags.length > 1 && (
              <div className="flex flex-wrap items-center gap-2 mb-4">
                <button
                  onClick={() => setSelectedTag(null)}
                  className={cn(
                    "px-3 py-1.5 text-sm rounded-full border transition-all",
                    !selectedTag
                      ? "bg-[var(--color-primary)] border-[var(--color-primary)] text-[var(--color-bg)]"
                      : "border-[var(--color-border)] text-[var(--color-text-muted)] hover:border-[var(--color-primary)] hover:text-[var(--color-text)]"
                  )}
                >
                  All ({sortedPortfolio.length})
                </button>
                {portfolioTags.map(({ tag, count }) => (
                  <button
                    key={tag}
                    onClick={() => setSelectedTag(selectedTag === tag ? null : tag)}
                    className={cn(
                      "px-3 py-1.5 text-sm rounded-full border transition-all",
                      selectedTag === tag
                        ? "bg-[var(--color-primary)] border-[var(--color-primary)] text-[var(--color-bg)]"
                        : "border-[var(--color-border)] text-[var(--color-text-muted)] hover:border-[var(--color-primary)] hover:text-[var(--color-text)]"
                    )}
                  >
                    {toTitleCase(tag)} ({count})
                  </button>
                ))}
              </div>
            )}

            {showFilters && (
              <div className="flex flex-wrap items-center justify-center gap-2 mb-6">
                <span className="text-sm text-[var(--color-text-muted)] font-medium">
                  Show:
                </span>
                <button
                  onClick={() => toggleContentRating("safe")}
                  className={cn(
                    "inline-flex items-center gap-1.5 px-4 py-2 rounded-full border-2 text-sm font-medium transition-all",
                    selectedContentRatings.includes("safe")
                      ? ratingConfig.safe.activeClass
                      : "border-[var(--color-border)] bg-transparent text-[var(--color-text-muted)] hover:border-[var(--color-primary)] hover:text-[var(--color-text)]"
                  )}
                >
                  <span>{ratingConfig.safe.icon}</span>
                  {ratingConfig.safe.label}
                </button>
                {showSuggestive && (
                  <button
                    onClick={() => toggleContentRating("suggestive")}
                    className={cn(
                      "inline-flex items-center gap-1.5 px-4 py-2 rounded-full border-2 text-sm font-medium transition-all",
                      selectedContentRatings.includes("suggestive")
                        ? ratingConfig.suggestive.activeClass
                        : "border-[var(--color-border)] bg-transparent text-[var(--color-text-muted)] hover:border-[var(--color-primary)] hover:text-[var(--color-text)]"
                    )}
                  >
                    <span>{ratingConfig.suggestive.icon}</span>
                    {ratingConfig.suggestive.label}
                  </button>
                )}
                {showNsfw && (
                  <button
                    onClick={() => toggleContentRating("nsfw")}
                    className={cn(
                      "inline-flex items-center gap-1.5 px-4 py-2 rounded-full border-2 text-sm font-medium transition-all",
                      selectedContentRatings.includes("nsfw")
                        ? ratingConfig.nsfw.activeClass
                        : "border-[var(--color-border)] bg-transparent text-[var(--color-text-muted)] hover:border-[var(--color-primary)] hover:text-[var(--color-text)]"
                    )}
                  >
                    <span>{ratingConfig.nsfw.icon}</span>
                    {ratingConfig.nsfw.label}
                  </button>
                )}
              </div>
            )}

            <ThumbnailCarousel
              images={carouselImages}
              artist={artist}
              onImageClick={handleImageClick}
              isLoading={isLoading}
            />

            {sortedPortfolio.length > 0 && (
              <div className="mt-4 text-center">
                <Link
                  to={`/artist/${slug}/gallery`}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[var(--color-bg-card)] border border-[var(--color-border)] text-[var(--color-text-muted)] hover:text-[var(--color-primary)] hover:border-[var(--color-primary)] transition-colors text-sm"
                >
                  <LayoutGrid className="h-4 w-4" />
                  View all {sortedPortfolio.length} artworks
                </Link>
              </div>
            )}
          </div>
        </div>

        {/* Reviews Section */}
        <ArtistReviewsSection artistId={artist.id} artistName={artist.name} />
      </section>

      {/* Lightbox */}
      {lightboxIndex !== null && (
        <Lightbox
          images={lightboxImages}
          currentIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onNavigate={setLightboxIndex}
        />
      )}

      {/* Waitlist Modal */}
      <WaitlistModal
        isOpen={isWaitlistOpen}
        onClose={() => setIsWaitlistOpen(false)}
        artist={{ id: artist.id, name: artist.name }}
      />
    </div>
  );
}
